import { useRef, useEffect, useState, useCallback } from "react";

interface WaveformTrimmerProps {
  audioUrl: string;
  duration?: number; // If provided, skip decode for long audio
  onTrim: (startTime: number, endTime: number) => void;
}

const WAVEFORM_DECODE_MAX = 120; // Only decode waveform for audio ≤ 2 minutes

export function WaveformTrimmer({ audioUrl, duration: knownDuration, onTrim }: WaveformTrimmerProps) {
  // If we know the duration is long, skip straight to range slider mode
  const skipDecode = knownDuration != null && knownDuration > WAVEFORM_DECODE_MAX;

  if (skipDecode) {
    return <RangeSliderTrimmer duration={knownDuration} onTrim={onTrim} audioUrl={audioUrl} />;
  }

  return <WaveformDecodeTrimmer audioUrl={audioUrl} knownDuration={knownDuration} onTrim={onTrim} />;
}

// ── Range Slider (for long audio) ──────────────────────────────────────

function RangeSliderTrimmer({
  duration,
  onTrim,
  audioUrl,
}: {
  duration: number;
  onTrim: (start: number, end: number) => void;
  audioUrl: string;
}) {
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(Math.min(30, duration));
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);

  // Notify parent of initial trim
  useEffect(() => {
    onTrim(0, Math.min(30, duration));
  }, []);

  const handleStartChange = (val: number) => {
    const clamped = Math.max(0, Math.min(val, endTime - 1));
    setStartTime(clamped);
    onTrim(clamped, endTime);
  };

  const handleEndChange = (val: number) => {
    const clamped = Math.min(duration, Math.max(val, startTime + 1));
    setEndTime(clamped);
    onTrim(startTime, clamped);
  };

  const togglePreview = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.currentTime = startTime;
      audioRef.current.play();
      setPlaying(true);
    }
  };

  // Stop at end time
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const check = () => {
      if (audio.currentTime >= endTime) {
        audio.pause();
        setPlaying(false);
      }
    };
    audio.addEventListener("timeupdate", check);
    return () => audio.removeEventListener("timeupdate", check);
  }, [endTime]);

  const selectedDuration = endTime - startTime;

  return (
    <div className="space-y-3">
      <audio ref={audioRef} src={audioUrl} preload="none" />

      {/* Visual range bar */}
      <div className="relative h-12 bg-slate-800 rounded-lg overflow-hidden">
        {/* Selected region */}
        <div
          className="absolute top-0 bottom-0 bg-indigo-500/30 border-x-2 border-indigo-400"
          style={{
            left: `${(startTime / duration) * 100}%`,
            width: `${(selectedDuration / duration) * 100}%`,
          }}
        />
        {/* Time markers */}
        {Array.from({ length: Math.min(10, Math.ceil(duration / 60)) + 1 }).map((_, i) => {
          const time = i * 60;
          if (time > duration) return null;
          return (
            <div
              key={i}
              className="absolute top-0 bottom-0 border-l border-slate-600"
              style={{ left: `${(time / duration) * 100}%` }}
            >
              <span className="absolute -bottom-0.5 left-1 text-[10px] text-slate-600">
                {formatTime(time)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Controls */}
      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Start</label>
          <TimeInput value={startTime} max={endTime - 1} onChange={handleStartChange} />
        </div>

        <button
          onClick={togglePreview}
          className="btn-ghost text-xs px-3 py-1.5 mt-4"
        >
          {playing ? "⏹ Stop" : "▶ Preview"}
        </button>

        <div>
          <label className="text-xs text-slate-500 block mb-1">End</label>
          <TimeInput value={endTime} max={duration} onChange={handleEndChange} />
        </div>
      </div>

      {/* Slider for quick adjustment */}
      <div className="space-y-1">
        <input
          type="range"
          min={0}
          max={duration}
          step={0.5}
          value={startTime}
          onChange={(e) => handleStartChange(parseFloat(e.target.value))}
          className="w-full accent-indigo-500 h-1"
        />
        <input
          type="range"
          min={0}
          max={duration}
          step={0.5}
          value={endTime}
          onChange={(e) => handleEndChange(parseFloat(e.target.value))}
          className="w-full accent-indigo-400 h-1"
        />
      </div>

      <p className="text-xs text-slate-500 text-center">
        {selectedDuration.toFixed(1)}s selected — voice references work best with 6–30s of clean speech
      </p>
    </div>
  );
}

// ── Time Input ─────────────────────────────────────────────────────────

function TimeInput({
  value,
  max,
  onChange,
}: {
  value: number;
  max: number;
  onChange: (val: number) => void;
}) {
  const [text, setText] = useState(formatTime(value));

  useEffect(() => {
    setText(formatTime(value));
  }, [value]);

  const handleBlur = () => {
    const parsed = parseTime(text);
    if (parsed != null && parsed >= 0 && parsed <= max) {
      onChange(parsed);
    } else {
      setText(formatTime(value));
    }
  };

  return (
    <input
      type="text"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => e.key === "Enter" && handleBlur()}
      className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-center font-mono text-slate-300 focus:border-indigo-500 focus:outline-none"
      placeholder="0:00"
    />
  );
}

function parseTime(s: string): number | null {
  const parts = s.trim().split(":");
  if (parts.length === 2) {
    const m = parseInt(parts[0], 10);
    const sec = parseFloat(parts[1]);
    if (!isNaN(m) && !isNaN(sec)) return m * 60 + sec;
  }
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

// ── Waveform Decode Trimmer (for short audio) ──────────────────────────

function WaveformDecodeTrimmer({
  audioUrl,
  knownDuration,
  onTrim,
}: {
  audioUrl: string;
  knownDuration?: number;
  onTrim: (start: number, end: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [waveformData, setWaveformData] = useState<Float32Array | null>(null);
  const [duration, setDuration] = useState(knownDuration || 0);
  const [startHandle, setStartHandle] = useState(0);
  const [endHandle, setEndHandle] = useState(1);
  const [dragging, setDragging] = useState<"start" | "end" | null>(null);
  const [playing, setPlaying] = useState(false);
  const [playbackPos, setPlaybackPos] = useState(0);
  const [error, setError] = useState(false);

  // Decode audio and extract waveform
  useEffect(() => {
    const ctx = new AudioContext();
    const controller = new AbortController();

    fetch(audioUrl, { signal: controller.signal })
      .then((res) => res.arrayBuffer())
      .then((buf) => ctx.decodeAudioData(buf))
      .then((decoded) => {
        setDuration(decoded.duration);
        const channelData = decoded.getChannelData(0);
        const samples = 500;
        const blockSize = Math.floor(channelData.length / samples);
        const downsampled = new Float32Array(samples);
        for (let i = 0; i < samples; i++) {
          let sum = 0;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(channelData[i * blockSize + j]);
          }
          downsampled[i] = sum / blockSize;
        }
        setWaveformData(downsampled);
        setEndHandle(1);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Waveform decode failed:", err);
          setError(true);
        }
      });

    return () => {
      controller.abort();
      ctx.close();
    };
  }, [audioUrl]);

  // Playback controls
  const togglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.currentTime = startHandle * duration;
      audio.play();
      setPlaying(true);
    }
  }, [playing, startHandle, duration]);

  // Stop playback at end handle, track position
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => {
      if (duration > 0) {
        setPlaybackPos(audio.currentTime / duration);
      }
      if (audio.currentTime >= endHandle * duration) {
        audio.pause();
        setPlaying(false);
      }
    };
    const onEnded = () => setPlaying(false);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
    };
  }, [endHandle, duration]);

  // Draw waveform
  useEffect(() => {
    if (!canvasRef.current || !waveformData) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    const { width, height } = canvas;

    ctx.clearRect(0, 0, width, height);

    const startX = startHandle * width;
    const endX = endHandle * width;

    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(0, 0, startX, height);
    ctx.fillRect(endX, 0, width - endX, height);

    const barWidth = width / waveformData.length;
    const maxVal = Math.max(...Array.from(waveformData)) || 1;

    for (let i = 0; i < waveformData.length; i++) {
      const x = i * barWidth;
      const barHeight = (waveformData[i] / maxVal) * height * 0.8;
      const isSelected = x >= startX && x <= endX;
      ctx.fillStyle = isSelected ? "#6366f1" : "#334155";
      ctx.fillRect(x, (height - barHeight) / 2, Math.max(1, barWidth - 1), barHeight);
    }

    // Playback position indicator
    if (playing && playbackPos >= startHandle && playbackPos <= endHandle) {
      const playX = playbackPos * width;
      ctx.fillStyle = "#f59e0b";
      ctx.fillRect(playX - 1, 0, 2, height);
    }

    ctx.fillStyle = "#818cf8";
    ctx.fillRect(startX - 2, 0, 4, height);
    ctx.fillRect(endX - 2, 0, 4, height);

    [startX, endX].forEach((x) => {
      ctx.beginPath();
      ctx.arc(x, height / 2, 8, 0, Math.PI * 2);
      ctx.fillStyle = "#6366f1";
      ctx.fill();
      ctx.strokeStyle = "#c7d2fe";
      ctx.lineWidth = 2;
      ctx.stroke();
    });
  }, [waveformData, startHandle, endHandle, playing, playbackPos]);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragging || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));

      if (dragging === "start") {
        setStartHandle(Math.min(pos, endHandle - 0.02));
      } else {
        setEndHandle(Math.max(pos, startHandle + 0.02));
      }
    },
    [dragging, startHandle, endHandle]
  );

  const handlePointerUp = useCallback(() => {
    setDragging(null);
    onTrim(startHandle * duration, endHandle * duration);
  }, [startHandle, endHandle, duration, onTrim]);

  useEffect(() => {
    if (dragging) {
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      return () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };
    }
  }, [dragging, handlePointerMove, handlePointerUp]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const distStart = Math.abs(pos - startHandle);
    const distEnd = Math.abs(pos - endHandle);
    setDragging(distStart < distEnd ? "start" : "end");
  };

  // If decode failed, fall back to range slider
  if (error && knownDuration) {
    return <RangeSliderTrimmer duration={knownDuration} onTrim={onTrim} audioUrl={audioUrl} />;
  }

  if (!waveformData) {
    return (
      <div className="card animate-pulse h-32 flex items-center justify-center">
        <span className="text-slate-500 text-sm">Loading waveform…</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <audio ref={audioRef} src={audioUrl} preload="auto" />
      <div
        ref={containerRef}
        onPointerDown={handlePointerDown}
        className="card p-0 overflow-hidden cursor-col-resize touch-none"
      >
        <canvas ref={canvasRef} width={800} height={120} className="w-full h-24" />
      </div>
      <div className="flex justify-between items-center text-xs text-slate-500">
        <span>{formatTime(startHandle * duration)}</span>
        <button
          onClick={togglePlayback}
          className="px-3 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors text-xs font-medium"
        >
          {playing ? "⏹ Stop" : "▶ Play"}
        </button>
        <span className="text-indigo-400">
          {formatTime((endHandle - startHandle) * duration)} selected
        </span>
        <span>{formatTime(endHandle * duration)}</span>
      </div>
    </div>
  );
}

function formatTime(s: number) {
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
}
